<script setup lang="ts">
import { Button } from '@/components/ui/button'
import { useFormPlus } from '@/ui-plus/form/useFormPlus'
import { Loader2 } from 'lucide-vue-next'
import { authGlobalApiClient } from '@api-client/auth_global';
import { showTurnstile } from '@/ui-plus/dialog/showTurnstile';
import { ref } from 'vue';

const emit = defineEmits<{
    onError: [errorMessage: string];
    onSuccess: [];
}>();

const { isLoading, globalError, handleError, withSubmit } = useFormPlus();

const sessionUrl = ref<string | null>(null);
const sessionStarted = ref(false);
const verificationStatus = ref<string | null>(null);

async function getTurnstileToken() {
    try {
        return await showTurnstile(import.meta.env.PUBLIC_TURNSTILE_SITE_KEY);
    } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        emit('onError', errorMessage);
        return null;
    }
}

async function onStartSession() {
    await withSubmit(async () => {
        const turnstile = await getTurnstileToken();
        if (!turnstile) return;

        const { data, error } = await (await authGlobalApiClient.id_verification.create_session.$post({
            form: { turnstile }
        })).json()

        if (error) {
            if (error.code === 'ALREADY_VERIFIED_ID') {
                emit('onSuccess');
            } else {
                handleError(error);
            }
        } else {
            sessionUrl.value = data.url;
            sessionStarted.value = true;
            verificationStatus.value = null;
        }
    })
}

async function onCheckStatus() {
    await withSubmit(async () => {
        // No turnstile — status polling is rate-limited server-side
        const { data, error } = await (await authGlobalApiClient.id_verification.get_sessions.$post({
            form: {}
        })).json()

        if (error) {
            handleError(error);
            return;
        }

        verificationStatus.value = data.status ?? data.message ?? 'Unknown';

        if (data.isVerified) {
            emit('onSuccess');
        }
    })
}

function openSessionUrl() {
    if (sessionUrl.value) {
        window.open(sessionUrl.value, '_blank');
    }
}
</script>

<template>
    <div class="py-10 space-y-6">
        <div v-if="!sessionStarted">
            <h2 class="text-lg font-bold">
                Verify your identity with a government-issued ID.
            </h2>
            <p class="text-sm mb-6">
                This is a one-time only process.
                <br>
                It will not be required in the future.
            </p>
            <Button type="button"
                    :disabled="isLoading"
                    class="w-full"
                    @click="onStartSession">
                <Loader2 v-if="isLoading"
                         class="animate-spin" />
                Start ID Verification
            </Button>
        </div>

        <div v-else
             class="space-y-6">
            <p class="text-sm text-muted-foreground">
                Always prefer a mobile device to get better photo quality.
                <br>
                A verification link has been sent to your email.
                <br>
                You can either click the link in the email or open the link below to continue on the current device.
            </p>

            <Button type="button"
                    variant="outline"
                    class="w-full"
                    :disabled="!sessionUrl"
                    @click="openSessionUrl">
                Open verification link
            </Button>

            <Button type="button"
                    :disabled="isLoading"
                    class="w-full"
                    @click="onCheckStatus">
                <Loader2 v-if="isLoading"
                         class="animate-spin" />
                Check verification status
            </Button>

            <p v-if="verificationStatus"
               class="text-sm font-medium">
                Status: {{ verificationStatus }}
            </p>
        </div>

        <div class="text-sm font-medium text-destructive"
             v-if="globalError">
            {{ globalError }}
        </div>
    </div>
</template>
